import { FooterAviso } from '@/global/components/DataGrid/components/DataTable/Footer'
import { SwitchSelect } from '@/global/components/SwitchSelect'
import { TabContext, TabPanel } from '@mui/lab'
import dayjs from 'dayjs'
import { Button } from 'global/components/Button'
import ContentGuide from 'global/guides/ContentGuide'
import { hasPermission } from 'hooks/useBackendMenu'
import { useState } from 'react'
import { Link, Outlet, useNavigate, useOutletContext } from 'react-router-dom'
import {
    type AberturaCompetencia,
    type FechamentoCompetencia,
    IFechamento,
    alteracaoCompetencia,
} from 'service/api'
import { useStore } from 'store'
import { getColumns } from 'templates/ManutencaoDoPonto/FechamentoAberturaCompetencia/constants/columns'

export enum TabCompetencia {
    ABERTURA = 'Abertura',
    FECHAMENTO = 'Fechamento',
}

export default function FechamentoCompetencia() {
    const [tab, setTab] = useState<TabCompetencia>(TabCompetencia.FECHAMENTO)
    const navigate = useNavigate()
    const empresa = useStore(state => state.company)
    const empresaId = useStore(state => state.companyId)
    const competencia = dayjs(empresa?.competenciaAtual ?? new Date()).format(
        'MM/YYYY',
    )
    const competenciaAnterior = dayjs(empresa?.competenciaAtual ?? new Date())
        .subtract(1, 'month')
        .format('MM/YYYY')
    const { data: fechamentos, isLoading: isLoadingFechamentos } =
        alteracaoCompetencia.fechamento.useFetch({
            enabled: empresaId > 0 && tab === TabCompetencia.FECHAMENTO,
            queryParams: { empresaId },
        })
    const { data: aberturas, isLoading: isLoadingAberturas } =
        alteracaoCompetencia.abertura.useFetch({
            enabled: empresaId > 0 && tab === TabCompetencia.ABERTURA,
            queryParams: { empresaId },
        })
    const { data: proximoFechamento } =
        alteracaoCompetencia.proximoFechamento.useFetch<IFechamento>({
            queryParams: {
                empresaId,
            },
            enabled: empresaId > 0,
        })

    const grid = [
        {
            tabPanel: TabCompetencia.FECHAMENTO,
            rows: (fechamentos || []).sort(
                (a, b) =>
                    dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf(),
            ),
            columns: getColumns('FECHADA'),
        },
        {
            tabPanel: TabCompetencia.ABERTURA,
            rows: (aberturas || []).sort(
                (a, b) =>
                    dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf(),
            ),
            columns: getColumns('ABERTA'),
        },
    ]

    return (
        <ContentGuide>
            <ContentGuide.Card>
                <ContentGuide.Header
                    label={[
                        'Manutenção do ponto',
                        'Fechamento de competência',
                        tab,
                    ]}
                >
                    <ContentGuide.Filtros SelectEmpresas childFirst>
                        <SwitchSelect
                            label="Ação"
                            className="w-40"
                            value={tab}
                            options={[
                                {
                                    value: TabCompetencia.FECHAMENTO,
                                    label: 'Fechamento',
                                },
                                {
                                    value: TabCompetencia.ABERTURA,
                                    label: 'Abertura',
                                },
                            ]}
                            onChange={setTab}
                        />
                    </ContentGuide.Filtros>
                </ContentGuide.Header>
                <div className="size-full">
                    <TabContext value={tab}>
                        {grid.map(({ rows, columns, tabPanel }, index) => (
                            <TabPanel key={index} value={tabPanel}>
                                <div className="h-[calc(100vh-12rem)] w-[calc(100%+3rem)] -mx-6 -my-6">
                                    <ContentGuide.DataGrid
                                        {...{ rows, columns }}
                                        loading={
                                            isLoadingFechamentos ||
                                            isLoadingAberturas
                                        }
                                        ToolbarProps={{
                                            exportButtonProps: {
                                                fileName: `exportacao-${tab.toLowerCase()}-competencia`,
                                            },
                                        }}
                                        components={{
                                            ...(proximoFechamento?.isNear && {
                                                Footer: () => (
                                                    <FooterAviso>
                                                        Esse é o mês de
                                                        encerramento do período
                                                        atual do banco de horas.
                                                        Não se esqueça de fazer
                                                        os ajustes necessários
                                                        antes de fechar a
                                                        competência, como{' '}
                                                        <Link to="/manutencao-ponto/digitacao">
                                                            ajustes de horário
                                                        </Link>{' '}
                                                        e{' '}
                                                        <Link to="/manutencao-ponto/pagamento-horas">
                                                            pagamento de horas
                                                        </Link>
                                                        .
                                                    </FooterAviso>
                                                ),
                                            }),
                                            Toolbar: () =>
                                                hasPermission([
                                                    'create',
                                                    'FechamentoDeCompetência',
                                                ]) ? (
                                                    <>
                                                        <div
                                                            style={{ flex: 1 }}
                                                        />
                                                        <Button
                                                            sx={{
                                                                ml: 'auto',
                                                            }}
                                                            onClick={() =>
                                                                navigate(
                                                                    'alterar',
                                                                )
                                                            }
                                                        >
                                                            {tab ===
                                                            TabCompetencia.FECHAMENTO
                                                                ? `Fechar competência ${competencia}`
                                                                : `Abrir competência ${competenciaAnterior}`}
                                                        </Button>
                                                    </>
                                                ) : null,
                                        }}
                                    />
                                </div>
                            </TabPanel>
                        ))}
                    </TabContext>
                </div>
            </ContentGuide.Card>
            <Outlet
                context={{
                    tab,
                    fechamentos: grid[0].rows,
                    aberturas: grid[1].rows,
                }}
            />
        </ContentGuide>
    )
}

export function useAberturaFechamentoCompetenciaOutletContext() {
    return useOutletContext<{
        tab: TabCompetencia
        aberturas?: AberturaCompetencia[]
        fechamentos?: FechamentoCompetencia[]
    }>()
}
